export interface CookieInstance {
  getValue: (key: string) => string | null;
}

// If this list ever changes, please ensure that change is reflected in this config in order to allow non-sb systems to work:
// https://configuration.skyscannertools.net/components/scaffolding/ShowCookieBanner#/
// Used here in request-context: https://github.skyscannertools.net/web-engineering/request-context/blob/7b3ea23a44a3974beefd7b9c6b1de03949fb701f/src/resolvers/cookie-resolver.js#L200
const regulatedMarkets = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'UK',
  'NO',
  'LI',
  'IS',
];

export type MarketCode = string;

// We are required by IAB TCF policy to remind users about their consent choices at least once every 13 months.
// If the consent decision is more than 13 months old, the TC string will no longer be considered valid by Google
// and Google will not serve ads to that user. We will set this to 12 months.
export const GDPR_COOKIE_MAX_AGE_IN_DAYS = 365;
export const NON_REGULATED_MARKET_FALLBACK_OPTIN_VALUE = true;
export const REGULATED_MARKET_FALLBACK_OPTIN_VALUE = false;
export const TC_STRING_DEFAULT_VALUE = '';
export const PURPOSE_SCOPED_ID_VERSION = 4; // Used with enablePurposeScopedIdService to trigger re-consent
export const LATEST_VERSION = 3;
export const GDPR_COOKIE_NAME = 'gdpr';
export const IS_GDPR_ACTIVE_FIELD_NAME = 'isGdprActiveInMarket' as const;
export const PREFERENCES = Object.freeze({
  OPTIN: 'optin',
  INFORMATION: 'information',
  ADVERTS: 'adverts',
  VERSION: 'version',
  TC_STRING: 'tcString',
} as const);

export type PreferenceKey = (typeof PREFERENCES)[keyof typeof PREFERENCES];
export type PreferenceName = PreferenceKey | typeof IS_GDPR_ACTIVE_FIELD_NAME;
export type MarketDefaults = Readonly<{
  [PREFERENCES.INFORMATION]: boolean;
  [PREFERENCES.ADVERTS]: boolean;
  [PREFERENCES.TC_STRING]: string;
  [PREFERENCES.VERSION]: number;
}>;

export type TrackingPreferences = MarketDefaults & {
  [IS_GDPR_ACTIVE_FIELD_NAME]: boolean;
};

export const NON_REGULATED_MARKET_DEFAULTS = Object.freeze({
  [PREFERENCES.INFORMATION]: NON_REGULATED_MARKET_FALLBACK_OPTIN_VALUE,
  [PREFERENCES.ADVERTS]: NON_REGULATED_MARKET_FALLBACK_OPTIN_VALUE,
  [PREFERENCES.TC_STRING]: TC_STRING_DEFAULT_VALUE,
  [PREFERENCES.VERSION]: LATEST_VERSION,
} satisfies MarketDefaults);

export const REGULATED_MARKET_DEFAULTS = Object.freeze({
  [PREFERENCES.INFORMATION]: REGULATED_MARKET_FALLBACK_OPTIN_VALUE,
  [PREFERENCES.ADVERTS]: REGULATED_MARKET_FALLBACK_OPTIN_VALUE,
  [PREFERENCES.TC_STRING]: TC_STRING_DEFAULT_VALUE,
  [PREFERENCES.VERSION]: LATEST_VERSION,
} satisfies MarketDefaults);

const toBoolean = (str: string | undefined | null) => (str || '') === 'true';

// Late-bound reference to avoid circular import with ketch-consent/cmp-state.
// Registered by ketch-consent.ts during module initialization. Returns
// undefined when not in Ketch mode, or when Ketch mode is active but no
// jurisdiction is known yet (e.g. no consent cookie) - in both cases
// isRegulatedMarket falls back to the legacy market list below.
let getKnownKetchJurisdiction: () => string | undefined = () => undefined;

export const registerKetchJurisdictionLookup = (
  fn: () => string | undefined,
): void => {
  getKnownKetchJurisdiction = fn;
};

const isRegulatedMarket = (market?: string): boolean => {
  const jurisdiction = getKnownKetchJurisdiction();
  if (jurisdiction !== undefined) {
    return jurisdiction.toUpperCase() === 'GDPR';
  }
  return market ? regulatedMarkets.includes(market.toUpperCase()) : true;
};

//
// @deprecated since version 1.1.36, will be deleted in subsequent versions
//
// Temporary function to enable Transparency & Consent Framework functionality
// feature switch. Client-side use only. To be removed post successful rollout
// of TCF functionality.
// eslint-disable-next-line @typescript-eslint/no-empty-function
const setTCFEnabled = (_tcfEnabled = false): void => {};

const attachGdprActiveField = (
  market: string,
  prefs: MarketDefaults | TrackingPreferences,
): TrackingPreferences => ({
  ...prefs,
  [IS_GDPR_ACTIVE_FIELD_NAME]: isRegulatedMarket(market),
});

const getFallbackOptinValue = (market: string): boolean =>
  isRegulatedMarket(market)
    ? REGULATED_MARKET_FALLBACK_OPTIN_VALUE
    : NON_REGULATED_MARKET_FALLBACK_OPTIN_VALUE;

/**
 * Retrieves the version number from the given cookie.
 * Cookie is expected to be either MultiValueCookie from 'skyscanner-cookie-parser' or MultiValueCookie from 'saddlebag-cookie'.
 * The node implementation uses 'skyscanner-cookie-parser', while the browser implementation uses 'saddlebag-cookie'.
 * When this function is used it is not clear which implementation is being used, hence this comment.
 *
 * @param {MultiValueCookie} cookie - MultiValueCookie
 * @returns {number} The version number extracted from the cookie, defaults to 0.
 */
const getVersion = (cookie: CookieInstance): number =>
  parseInt(cookie.getValue(PREFERENCES.VERSION) as string, 10) || 0;

/**
 * Gets the preference value for the given preference key from the provided cookie.
 * Cookie is expected to be either MultiValueCookie from 'skyscanner-cookie-parser' or MultiValueCookie from 'saddlebag-cookie'.
 * The node implementation uses 'skyscanner-cookie-parser', while the browser implementation uses 'saddlebag-cookie'.
 * When this function is used it is not clear which implementation is being used, hence this comment.
 *
 * @param {MultiValueCookie} gdprCookie - MultiValueCookie
 * @param {string} market - market code (e.g., 'US', 'UK', etc.)
 * @param {string} preference - Preference key to retrieve the value for.
 * @returns {boolean} Preference value for the given preference key.
 */
const getPreference = (
  gdprCookie: CookieInstance,
  market: string,
  preference: PreferenceKey,
): boolean => {
  const preferenceValue = gdprCookie.getValue(preference);
  /**
   * Fixing #inc-2024-09-23-travellers-consent-preferences-being-ignored
   * Date: 2024-09-26
   * `false` preference values were incorrectly being set to `null` and as a result the `getFallbackOptinValue` was being used.
   * Within non-regulated markets the return value of `getFallbackOptinValue` is `true` thus causing a discrepancy between traveler intent and behaviour.
   * This function now handles that reality (of `null` values representing `false`) and returns `false` if the value if a `null` is detected.
   * It also makes clear that when `undefined` values are found that the fallback for the market should be used.
   * After 12 months, all traveller cookies will have expired and thus this fix will no longer be needed. However, it's highly likely that this will remain in place for the foreseeable future.
   */
  if (preferenceValue === undefined) {
    return getFallbackOptinValue(market);
  }
  if (preferenceValue === null) {
    return false;
  }
  return toBoolean(preferenceValue);
};

/**
 * Retrieves all preferences from the given cookie.
 * Cookie is expected to be either MultiValueCookie from 'skyscanner-cookie-parser' or MultiValueCookie from 'saddlebag-cookie'.
 * The node implementation uses 'skyscanner-cookie-parser', while the browser implementation uses 'saddlebag-cookie'.
 * When this function is used it is not clear which implementation is being used, hence this comment.
 *
 * @param {MultiValueCookie} gdprCookie - MultiValueCookie
 * @param {string} market - market code (e.g., 'US', 'UK', etc.)
 * @returns {object} preferences object containing all preference values.
 */
const getPreferencesFromCookie = (
  gdprCookie: CookieInstance,
  market: string,
): TrackingPreferences => {
  const information = getPreference(
    gdprCookie,
    market,
    PREFERENCES.INFORMATION,
  );
  const adverts = getPreference(gdprCookie, market, PREFERENCES.ADVERTS);

  return {
    [PREFERENCES.INFORMATION]: information,
    [PREFERENCES.ADVERTS]: adverts,
    [PREFERENCES.VERSION]: getVersion(gdprCookie),
    [IS_GDPR_ACTIVE_FIELD_NAME]: isRegulatedMarket(market),
    [PREFERENCES.TC_STRING]:
      // TC_STRING is expected to be a string value in the cookie, so we can safely cast it
      (gdprCookie.getValue(PREFERENCES.TC_STRING) as string) ||
      TC_STRING_DEFAULT_VALUE,
  };
};

export function isBrowserEnvironment(): boolean {
  return globalThis.window !== undefined;
}

export {
  getPreferencesFromCookie,
  attachGdprActiveField,
  setTCFEnabled,
  getVersion,
  isRegulatedMarket,
};
